import { getApiEndpoint } from "../utils/constants/api";
import { SHOP_SOURCES } from "../utils/constants/shopSources";
import { wfetch } from "../utils/fetch";

// The delivery API HTML-escapes string properties ("→&quot;), so merchandisingJson comes back as
// {&quot;items&quot;:…}. Decode entities before JSON.parse (a no-op for already-clean values).
const decodeEntities = (s) => {
    if (typeof document === "undefined") {
        return s
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&amp;/g, "&");
    }
    const t = document.createElement("textarea");
    t.innerHTML = s;
    return t.value;
};

// The visual merchandiser stores the curated product order (+ banners) as `merchandisingJson` on
// the category node. For external sources it lives on the synced node in the ecommerce-categories
// JCR workspace, addressable by source + externalId. Returns the ordered items array, or null.
export const getMerchandising = async (opts) => {
    const { source, categoryId, category } = opts;
    let raw;
    if (source === SHOP_SOURCES.JCR) {
        raw = category && category.merchandisingJson;
    } else {
        try {
            const res = await wfetch(
                getApiEndpoint("CATEGORIES", { queryParams: { "source[eq]": source, "externalId[eq]": categoryId } }),
            );
            const results = res?.data?.results || [];
            const node = results.find((n) => n.externalId === categoryId) || results[0];
            raw = node && node.merchandisingJson;
        } catch (e) {
            // Network / HTTP failure must not blank the whole grid — fall back to default order.
            return null;
        }
    }
    if (!raw || typeof raw !== "string") return null;
    try {
        const parsed = JSON.parse(decodeEntities(raw));
        return Array.isArray(parsed.items) ? parsed.items : null;
    } catch (e) {
        return null;
    }
};

export const getCategories = async (opts) => {
    const { source } = opts;

    // JCR source
    if (source === SHOP_SOURCES.JCR) {
        const { lang } = opts;

        const res = await wfetch(getApiEndpoint("CATEGORIES", { queryParams: { lang } }));

        return res?.data?.results;
    }

    // External sources
    const res = await wfetch(getApiEndpoint("EXTERNAL_CATEGORIES", { pathParams: { source } }));

    return res?.data?.results && composeCategoriesTree(res.data.results, source);
};

export const getCategory = async (opts) => {
    const { source, categoryId } = opts;

    // JCR source
    if (source === SHOP_SOURCES.JCR) {
        const { lang } = opts;

        const res = await wfetch(getApiEndpoint("CATEGORIES", { queryParams: { lang, "@path": categoryId } }));

        return res?.data?.results && res?.data?.results[0];
    }

    // External sources
    const res = await wfetch(getApiEndpoint("EXTERNAL_CATEGORIES", { pathParams: { source, categoryId: encodeURIComponent(categoryId) } }));

    return res?.data;
};

export const composeCategoriesTree = (categories, source) => {
    if (source === SHOP_SOURCES.JCR) return categories;

    const map = categories.reduce((acc, item) => ({ ...acc, [item["@id"]]: { ...item, "@nodes": [] } }), {});

    return Object.values(
        categories.reduce((acc, { "@id": id, parentId }) => {
            if (parentId && acc[parentId]) {
                acc[parentId][id] = acc[id];
                acc[parentId]["@nodes"].push(id);
            }
            return acc;
        }, map),
    ).filter(({ parentId }) => !parentId);
};

export const getCategoryTreeIds = (category) => {
    const ids = new Set([]);

    if (category["@id"]) ids.add(category["@id"]);

    if (category["@nodes"] && category["@nodes"].length)
        category["@nodes"].forEach((nodeName) => category[nodeName] && ids.add(...getCategoryTreeIds(category[nodeName])));

    return ids;
};
